/**
 * Footnotes at the foot of the tan sidebar, one list per page (pages 1–3).
 *
 * Weekly 02.10.2026: SQUAD manages the notes themselves. Each page gets its own list, and
 * the lines are free text — no automatic number, no raised digit. SQUAD writes "1 – …",
 * "1 | …" or nothing at all (a note under the PRI logo needs no number). Raised digits
 * (¹ ² ³) are only used inside the content (headings, table labels) and typed there by
 * SQUAD as plain Unicode characters.
 *
 * Until 05.10.2026 there was one global list, numbered by position. A document that still
 * holds only that list shows it on every page, as before, but without the automatic
 * markers — SQUAD-TEST already carried its own numbers, which the markers doubled.
 */

export const SQUAD_SIDEBAR_FOOTNOTE_PAGES = [1, 2, 3] as const;
export type SquadSidebarFootnotePage = (typeof SQUAD_SIDEBAR_FOOTNOTE_PAGES)[number];

export type SquadSidebarFootnoteKey = 'sidebarFootnotesPage1' | 'sidebarFootnotesPage2' | 'sidebarFootnotesPage3';

export function squadSidebarFootnoteKey(page: SquadSidebarFootnotePage): SquadSidebarFootnoteKey {
  return `sidebarFootnotesPage${page}` as SquadSidebarFootnoteKey;
}

/**
 * Placeholder wording for a new document, until SQUAD supplies the legal text. The numbers
 * are part of the text now, so SQUAD can change or drop them.
 */
export const DEFAULT_SIDEBAR_FOOTNOTES = [
  '¹ Erläuterungen siehe Disclaimer (letzte Seite)',
  '² Erläuterungen siehe Disclaimer (letzte Seite)',
  '³ Erläuterungen siehe Disclaimer (letzte Seite)',
].join('\n');

/**
 * The three page lists for the mapper. A page that was saved keeps its own text, even
 * when empty (clearing a page must stick). Otherwise the former global list, otherwise the
 * placeholder.
 */
export function resolveSquadSidebarFootnotes(
  additional: Record<string, unknown> | null | undefined,
): Record<SquadSidebarFootnoteKey, string> {
  const legacy = additional?.sidebarFootnotes;
  const fallback = typeof legacy === 'string' ? legacy : DEFAULT_SIDEBAR_FOOTNOTES;
  const out = {} as Record<SquadSidebarFootnoteKey, string>;
  for (const page of SQUAD_SIDEBAR_FOOTNOTE_PAGES) {
    const key = squadSidebarFootnoteKey(page);
    const own = additional?.[key];
    out[key] = typeof own === 'string' ? own : fallback;
  }
  return out;
}

/** One entry per non-empty line, exactly as typed — no marker is added. */
export function parseSquadFootnotes(value: unknown): string[] {
  return String(value ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}
